/**
 * Creative cell with senders/texts tooltip.
 */

import { useMemo, type FC } from 'react';
import * as RadixTooltip from '@radix-ui/react-tooltip';
import { Info, Loader2 } from 'lucide-react';

interface CreativeCellProps {
  senderNames: string[];
  smsTexts: string[];
  /** The Drop Generator is writing this row's creative right now. */
  filling?: boolean;
}

export const CreativeCell: FC<CreativeCellProps> = ({
  senderNames,
  smsTexts,
  filling,
}) => {
  const uniqueSenders = useMemo(() => Array.from(new Set(senderNames)), [senderNames]);
  const uniqueTexts = useMemo(() => Array.from(new Set(smsTexts)), [smsTexts]);
  const senderCount = uniqueSenders.length;
  const textCount = uniqueTexts.length;
  const hasSendersOrTexts = senderCount > 0 || textCount > 0;

  const inlineDisplay = useMemo(() => {
    if (!hasSendersOrTexts) return null;
    const senderLabel =
      senderCount === 0
        ? null
        : (() => {
            const combined = uniqueSenders.join(', ');
            if (combined.length <= 45 && senderCount <= 3) return combined;
            return `${senderCount} sender${senderCount === 1 ? '' : 's'}`;
          })();
    const textLabel = textCount === 0 ? null : `${textCount} text${textCount === 1 ? '' : 's'}`;
    return [senderLabel, textLabel].filter(Boolean).join(' · ');
  }, [uniqueSenders, senderCount, textCount, hasSendersOrTexts]);

  const tooltipContent = useMemo(() => {
    if (!hasSendersOrTexts) return null;
    const truncate = (s: string, len: number) => (s.length <= len ? s : `${s.slice(0, len)}…`);
    return (
      <div
        className="min-w-[280px] max-w-[520px] overflow-y-auto text-left"
        style={{ maxHeight: 'min(480px, var(--radix-tooltip-content-available-height, 480px))' }}
      >
        <div className="mb-3 border-b border-white/20 pb-2 text-xs font-semibold uppercase tracking-wider opacity-90">
          Creative snapshot
        </div>
        {senderCount > 0 && (
          <div className="mb-3">
            <div className="mb-1.5 flex items-center gap-2 text-xs font-medium opacity-90">
              Sender names
              <span className="rounded bg-white/20 px-1.5 py-0.5 text-[10px] font-normal">{senderCount}</span>
            </div>
            <ul className="space-y-1">
              {uniqueSenders.map((s, i) => (
                <li key={`${s}-${i}`} className="rounded bg-white/10 px-2 py-1.5 text-xs leading-relaxed">
                  {s}
                </li>
              ))}
            </ul>
          </div>
        )}
        {textCount > 0 && (
          <div>
            <div className="mb-1.5 flex items-center gap-2 text-xs font-medium opacity-90">
              SMS texts
              <span className="rounded bg-white/20 px-1.5 py-0.5 text-[10px] font-normal">{textCount}</span>
            </div>
            <ol className="space-y-1.5">
              {uniqueTexts.map((t, i) => (
                <li key={i} className="flex gap-2 rounded bg-white/10 px-2 py-1.5 text-xs leading-relaxed">
                  <span className="shrink-0 font-medium opacity-75">{i + 1}.</span>
                  <span className="min-w-0 break-words">{truncate(t, 200)}</span>
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
    );
  }, [uniqueSenders, uniqueTexts, hasSendersOrTexts, senderCount, textCount]);

  if (!hasSendersOrTexts) {
    if (filling)
      return (
        <span className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 shrink-0 animate-spin" />
          <span className="animate-pulse truncate">generating…</span>
        </span>
      );
    return <span className="min-w-0" aria-hidden />;
  }

  return (
    <span className="min-w-0">
      <RadixTooltip.Provider delayDuration={250} skipDelayDuration={150}>
        <RadixTooltip.Root delayDuration={250}>
          <RadixTooltip.Trigger asChild>
            <span
              className="flex min-w-0 cursor-help items-center gap-1 truncate rounded px-1 py-0.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
              aria-label="View full senders and SMS texts"
            >
              <Info className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{inlineDisplay}</span>
            </span>
          </RadixTooltip.Trigger>
          <RadixTooltip.Portal>
            <RadixTooltip.Content
              side="right"
              sideOffset={8}
              avoidCollisions
              collisionPadding={16}
              className="z-[100] max-w-[520px] min-w-[280px] rounded-md bg-foreground px-3 py-2 text-xs leading-relaxed text-background shadow-lg"
            >
              {tooltipContent}
            </RadixTooltip.Content>
          </RadixTooltip.Portal>
        </RadixTooltip.Root>
      </RadixTooltip.Provider>
    </span>
  );
};
